import React, { lazy, Suspense, useState, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Inter (body, theme default) and Outfit (FONT_HEADING in styles.ts): one import per weight the
// console renders. With 400 alone, every 600/700 was a browser faux bold, and every 500 (explicit,
// plus MUI's Button/Tab/table-head/h6/subtitle2 defaults) fell back to regular. Each file keeps
// all subsets; a face downloads only when its weight and unicode-range are actually rendered.
// Inter 300 is left out on purpose: MUI's h2 asks for it (OverviewPage's hero number), it renders
// at 400 today, and making it light is a design call rather than a font-loading fix.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/500.css";
import "@fontsource/outfit/600.css";
import "@fontsource/outfit/700.css";
// Domine (onboarding/auth headings). Self-hosted via @fontsource to match the rest of the
// app — no third-party fonts.googleapis.com request.
import "@fontsource/domine/400.css";
import "@fontsource/domine/500.css";
import "@fontsource/domine/600.css";
import "@fontsource/domine/700.css";

import { auth, warmUpPopupResolver } from "./lib/firebase";
import { PageId, SubscriptionStatus } from "./types";
import { resolveExtensionSubscription, type ExtensionSubscription } from "./lib/extensionSubscription";
import { parseLocationHash, parseHashUserId, parseHashPolicyTab, parseHashEffectiveSubject, hashForPage, historyIndexOf, withHistoryIndex, replaceUrl, PolicyTab } from "./lib/pageRouting";
import type { EffectiveSubject } from "./hooks/useEffectiveProtection";
import { isSafeDesktopCallback } from "./lib/desktopCallback";
import { accountProvider } from "./lib/accountProvider";
import { isTenantOverridden, onActiveTenantChangedInAnotherTab, setActiveTenantId } from "./lib/activeTenant";
import { readSignupIntent, signupStepFor, withoutSignupParams } from "./lib/signupIntent";
import { claimTokenFromHash, forgetClaimToken, readClaimToken, saveClaimToken } from "./lib/orgClaim";
import { CHROME_STORE_URL } from "./constants";
import { consoleKindFor, landingDecided, landingRedirect } from "./lib/consoleNav";
import { model2Redirect } from "./lib/protectedRouting";
import { protectedModelOf } from "./lib/protectedModel";
import { useAccessNotice } from "./hooks/useAccessNotice";
import { isPasswordResetLink, isVerifyEmailLink, passwordResetCode, verifyEmailCode, readUrlSecret } from "./lib/urlSecrets";
import { readStored } from "./lib/safeStorage";
import { isConsoleRole } from "./lib/tenantAccess";
import { deriveTenantAdmin } from "./hooks/useTenantAdmin";
import { loadOverviewPage } from "./lib/pageChunks";
import { clearPerUserStorage } from "./lib/userSession";
import { getLoginGradient } from "./styles";
import { primaryTextColor } from "./theme";

import { useThemeMode } from "./contexts/ThemeModeContext";

import { useAuth } from "./hooks/useAuth";
import { useSubscription } from "./hooks/useSubscription";
import { useConsoleOpenedEvent } from "./hooks/useConsoleOpenedEvent";
import { useSecureSites } from "./hooks/useSecureSites";
import { useBusinessTeam } from "./hooks/useBusinessTeam";
import { useExtension } from "./hooks/useExtension";
import { useJoinInvitation } from "./hooks/useJoinInvitation";
import { useOnboarding } from "./hooks/useOnboarding";
import { useHomeMetrics } from "./hooks/useHomeMetrics";
import { useWhatsNew } from "./hooks/useWhatsNew";
import { useExtensionWhitelist } from "./hooks/useExtensionWhitelist";
import { useOverview } from "./hooks/useOverview";
import { useRiskReport } from "./hooks/useRiskReport";
import { useSaasInventory } from "./hooks/useSaasInventory";
import { useOrgSettings } from "./hooks/useOrgSettings";
import { useOrgPolicyNotifier } from "./hooks/useOrgPolicyNotifier";
import { useBusinessGroups } from "./hooks/useBusinessGroups";
import { useMspAccess } from "./hooks/useMspAccess";
import { useDirectory } from "./hooks/useDirectory";
import { useMcpAuthRequest } from "./hooks/useMcpAuthRequest";
import { guardUrlNavigation } from "./hooks/useUnsavedChanges";
import {
  setUserProperties,
  calculateAnnualLTV,
  trackDesktopAppLogin,
  trackPurchase,
  trackPageView,
  trackExtensionPostInstallPageViewed,
  trackIndividualToWebStore,
  trackSignupPlanSelected,
} from "./lib/analytics";

import { captureReferral } from "./lib/referral";
import { AccountDeletedPage } from "./components/AccountDeletedPage";
import { DeletionReceipt } from "./lib/accountDeletion";
import { WhatsNewRelease, findReleaseForVersion } from "./data/whats-new";
import { AuthScreen } from "./components/onboarding/AuthScreen";
import type { LogoutSource } from "./lib/logoutSource";
import {
  ClaimOrgFlow,
  ConfirmAliasPage,
  McpAuthPage,
  ResetPasswordPage,
  SignupFlow,
  VerifyEmailPage,
  WhatsNewPage,
  prefetchRareViewsFor,
} from "./rareViews";

// ENG-3620: the signed-in console is its own chunk, so the sign-in screen, the reset and alias
// pages and the loading spinner no longer download it; it took the main bundle from 338 KB gzip to
// 230 KB (the console's chunk is 71 KB). The auth listener starts the import as soon as Firebase reports a signed-in user, so
// the chunk downloads while /subscription-status is out rather than after it. A rejected import (a
// tab left open across a deploy, ENG-2596) throws past Suspense to the root ErrorBoundary in
// index.tsx, which offers the reload.
const loadDashboard = () => import("./components/DashboardLayout");
const DashboardLayout = lazy(() => loadDashboard().then((m) => ({ default: m.DashboardLayout })));

/** The full-page spinner every pre-console wait shows, and the console's Suspense fallback. */
const FullPageSpinner: React.FC = () => {
  const theme = useTheme();
  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: getLoginGradient(theme),
    }}>
      <CircularProgress sx={{ color: primaryTextColor(theme) }} size={48} />
    </Box>
  );
};

/** ENG-3621: the pages that show the protected-site selection, so the only ones that poll it. */
const PAGES_SHOWING_SELECTION: ReadonlySet<PageId> = new Set<PageId>(['sites', 'policy']);

// ENG-2499/2527: resolution is table-driven (see lib/pageRouting.parseLocationHash) so every
// PageId the app writes round-trips, and the cold read + the runtime hashchange listener share
// one parse. Unknown/empty hash falls back to Home (the post-login landing).
const getInitialPage = (): PageId => parseLocationHash(window.location.hash);

/** ENG-4072: how long after pressing a sign-in button the auth listener's message counts as that sign-in. */
const EXPLICIT_LOGIN_WINDOW_MS = 60_000;

// Only allow the desktop auth redirect to target the app's own protocol or a localhost dev
// callback — never an arbitrary URL, which would exfiltrate the user's id/refresh tokens.

export const App: React.FC = () => {
  // ── Hooks ──
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // True while an extension-initiated logout (?logout=true) is in progress, so neither the
  // auth listener nor the extension-detect effect re-broadcast auth-complete to the extension.
  const isLoggingOutRef = useRef(false);
  // ENG-4072: when the person last pressed a sign-in button here (0 = not recently). The extension
  // ignores this site's passive `auth-complete` (a page load, the 45-minute refresh) after a Log out in
  // its popup, but not one that follows a login. A login on a page opened directly (no `?extensionId=`)
  // reaches the extension only through the auth listener below, not through the handlers, so the
  // listener reads this to mark its message explicit. Set before the sign-in starts, because Firebase
  // notifies the listener while the handler is still waiting; cleared when the sign-in fails.
  const explicitLoginAtRef = useRef(0);
  const { setForceLight, toggleMode } = useThemeMode();
  const authHook = useAuth();
  const subscription = useSubscription();
  // ENG-4027: a save refused because the Protected model changed re-reads the status (useSecureSites).
  const sites = useSecureSites(subscription.subscriptionStatus, subscription.fetchSubscriptionStatus);
  const team = useBusinessTeam();
  const home = useHomeMetrics();
  const whatsNew = useWhatsNew();
  const whitelist = useExtensionWhitelist();
  const overview = useOverview();
  // ENG-2563: hoisted like useOverview so the report persists across page navigation (RiskReportPage
  // unmounts on nav; keeping the hook here means returning to #report reuses the loaded report).
  const riskReport = useRiskReport();
  // ENG-3096: hoisted for the same reason as riskReport, above — SaasInventoryPage unmounts on nav,
  // so keeping the hook here means returning to #saas-inventory reuses the loaded inventory.
  const saasInventory = useSaasInventory();
  const groups = useBusinessGroups();
  const mspAccess = useMspAccess();
  // ENG-2586. Hoisted like the other org-admin hooks so its state survives navigation, but it fetches
  // nothing until the Directory page mounts (ENG-2571 keeps admin payloads off the login path).
  const directory = useDirectory();

  const onboarding = useOnboarding({
    authInitialized: authHook.authInitialized,
    userInfo: authHook.userInfo,
    setForceLight,
  });

  // ENG-2387: what the extension is told. Identical to the console's own status
  // until a tenant switch is active, at which point it is re-fetched unscoped so the
  // operator's Shield keeps following the operator's own tenant. See
  // lib/extensionSubscription.ts for why this separation is load-bearing.
  const [extensionSubscription, setExtensionSubscription] = useState<ExtensionSubscription | null>(null);

  const extension = useExtension({
    authInitialized: authHook.authInitialized,
    userInfo: authHook.userInfo,
    subscriptionStatus: subscription.subscriptionStatus,
    extensionSubscriptionStatus: extensionSubscription,
    fetchCustomSites: sites.fetchCustomSites,
    isLoggingOutRef,
  });

  // ENG-2671: declared AFTER useExtension because it needs the detected extension ids. What it tells,
  // and why each (the extension, the Risk Report, Overview, the console's own plan), is in the hook.
  const notifyOrgPolicy = useOrgPolicyNotifier({
    getAllExtensionIds: extension.getAllExtensionIds,
    invalidateRiskReport: riskReport.invalidate,
    invalidateOverview: overview.invalidate,
    refreshSubscriptionStatus: subscription.fetchSubscriptionStatus,
  });

  const orgSettings = useOrgSettings(notifyOrgPolicy);

  const joinInvitation = useJoinInvitation({
    userInfo: authHook.userInfo,
    subscriptionStatus: subscription.subscriptionStatus,
    loadingSubscription: subscription.loadingSubscription,
    authHook: {
      setEmail: authHook.setEmail,
      isSignUp: authHook.isSignUp,
      toggleSignUp: authHook.toggleSignUp,
      setFirstName: authHook.setFirstName,
      setLastName: authHook.setLastName,
    },
    fetchSubscriptionStatus: subscription.fetchSubscriptionStatus,
    fetchBusinessMembers: team.fetchBusinessMembers,
    fetchBusinessCustomSites: team.fetchBusinessCustomSites,
  });

  // ── Desktop app state ──
  const [isDesktopMode, setIsDesktopMode] = useState(false);
  const [desktopCallbackUrl, setDesktopCallbackUrl] = useState<string | null>(null);
  // ENG-2619: opened from a Workspace-managed (force-installed) extension via ?managed=1. The org owns
  // the session, so we suppress sign-out / switch-account here (no back door around the extension's
  // "managed users can't log out"). Seeded from sessionStorage so it survives the param strip + reloads
  // within this tab session; the extension re-sends ?managed=1 on each open, so it can't get stuck on.
  const [managed, setManaged] = useState<boolean>(() => {
    try { return sessionStorage.getItem('havenManaged') === '1'; } catch { return false; }
  });

  // ── Navigation ──
  const [activePage, setActivePage] = useState<PageId>(getInitialPage());
  // ENG-2522: the user-detail target rides in the hash query (`#user?id=<tenantUserId>`), separate
  // from activePage. Opaque tenantUserId only - never an email (no PII in the URL).
  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => parseHashUserId(window.location.hash));
  const openUserDetail = (tenantUserId: string) => { setSelectedUserId(tenantUserId); setActivePage('user'); };
  // ENG-3468: Policy's tab rides in the hash the same way (`#policy?tab=sites`), so a tab can be linked
  // to and Back steps between tabs. Null is Policy's default tab. Cleared on leaving Policy (below), so
  // the sidebar's Policy opens the default tab as it always has, and a Back to a tab restores it from
  // that entry's URL.
  const [policyTab, setPolicyTab] = useState<PolicyTab | null>(() => parseHashPolicyTab(window.location.hash));
  useEffect(() => { if (activePage !== 'policy') setPolicyTab(null); }, [activePage]);
  // ENG-3511: whose browser "What a browser gets" shows (`#effective?member=<id>`), the same way. Kept
  // while on that page, so Back between two members restores each from its entry's URL.
  const [effectiveSubject, setEffectiveSubject] = useState<EffectiveSubject | null>(() => parseHashEffectiveSubject(window.location.hash));
  useEffect(() => { if (activePage !== 'effective') setEffectiveSubject(null); }, [activePage]);
  const openEffective = (subject: EffectiveSubject) => { setEffectiveSubject(subject); setActivePage('effective'); };
  // ENG-4063: the group Protected sites opens on, under protectedModel 2, when an old
  // `#effective?group=` link was redirected there (lib/protectedRouting). Cleared on leaving the page.
  const [sitesScope, setSitesScope] = useState<string | null>(null);
  useEffect(() => { if (activePage !== 'sites') setSitesScope(null); }, [activePage]);
  // ENG-2691: false until the URL-sync effect has run once. Distinguishes "normalizing the URL we
  // were opened with" from "the user navigated", which decide replace vs push.
  const historyPrimed = useRef(false);
  // ENG-3376: the position of the entry in the address bar, in step with the stamp every entry the
  // console makes carries (lib/pageRouting historyIndexOf). Seeded from the current entry, whose state
  // survives a reload.
  const historyIndex = useRef(historyIndexOf(window.history.state) ?? 0);
  // ENG-3348: the pending "connect Claude" request, in state so the consent screen's Cancel can end it.
  const mcpAuth = useMcpAuthRequest();

  // ENG-2527: reverse-sync the URL → page. The one-way writer below only pushes activePage INTO
  // the hash, so at runtime a manual hash edit, browser back/forward, or an in-session deep-link
  // never switched pages. This listener parses the new hash and updates activePage.
  //
  // ENG-2691: `popstate` as well as `hashchange`. Now that the writer pushes real history entries,
  // back/forward moves BETWEEN them, and popstate is the event that reliably covers that — including
  // the leg where the fragment does not change at all, which emits no hashchange.
  //
  // No loop, and the reason is no longer "replaceState is silent" — the writer now pushes. It is the
  // writer's own `location.hash !== hash` guard: arriving here from a back/forward sets activePage to
  // whatever the URL already says, so the writer recomputes an identical hash and does nothing.
  //
  // ENG-3376: the change goes through the unsaved-changes guard, so while an editor holds unsaved
  // edits, Back/Forward, a swipe or a hash edit asks first, as a click in the console does. To refuse
  // one and leave history exactly as it was, the guard steps back the way the browser came, which
  // needs the distance: the difference between the positions stamped on the two entries.
  const shownHash = hashForPage(activePage, selectedUserId, policyTab, effectiveSubject);
  const shownHashRef = useRef(shownHash);
  shownHashRef.current = shownHash;
  // The entry the guard is stepping back to, so the popstate that step raises is not a navigation, and
  // a Discard changes chosen before that step has landed, to take once it has: taken earlier, it would
  // count from the wrong entry.
  const restoringTo = useRef<number | null>(null);
  const redoAfterRestore = useRef<number | null>(null);
  useEffect(() => {
    const syncPageFromUrl = () => {
      // An entry with no stamp is one the browser has just added for a hash typed into the address bar.
      // It follows the entry that was current, and is stamped now so a later Back or Forward to it can
      // be placed too.
      let index = historyIndexOf(window.history.state);
      if (index === null) {
        index = historyIndex.current + 1;
        window.history.replaceState(withHistoryIndex(window.history.state, index), '', window.location.href);
      }
      const delta = index - historyIndex.current;
      historyIndex.current = index;
      // Nothing moved: the hashchange that follows the popstate for the same step.
      if (delta === 0) return;
      const restoring = restoringTo.current;
      const redo = redoAfterRestore.current;
      restoringTo.current = null;
      redoAfterRestore.current = null;
      if (index === restoring) {
        if (redo !== null) window.history.go(redo);
        return;
      }
      const page = parseLocationHash(window.location.hash);
      const userId = parseHashUserId(window.location.hash);
      const tab = parseHashPolicyTab(window.location.hash);
      const subject = parseHashEffectiveSubject(window.location.hash);
      // The entry names the page already on screen, so there is nothing to show and nothing to lose.
      // ENG-3468: a Policy tab counts, so Back between two tabs is guarded like Back between pages.
      if (hashForPage(page, userId, tab, subject) === shownHashRef.current) return;
      guardUrlNavigation({
        apply: () => {
          setActivePage(page);
          setSelectedUserId(userId);
          setPolicyTab(tab);
          setEffectiveSubject(subject);
        },
        undo: () => {
          restoringTo.current = index - delta;
          window.history.go(-delta);
        },
        redo: () => {
          if (restoringTo.current !== null) redoAfterRestore.current = delta;
          else window.history.go(delta);
        },
      });
    };
    window.addEventListener('hashchange', syncPageFromUrl);
    window.addEventListener('popstate', syncPageFromUrl);
    return () => {
      window.removeEventListener('hashchange', syncPageFromUrl);
      window.removeEventListener('popstate', syncPageFromUrl);
    };
  }, []);

  // ENG-2573: the tenant selection is shared across tabs but only the switching tab reloads, so a
  // second tab went on rendering the previous tenant while every request it made carried the new
  // tenant's header — showing your own org and writing to a client's. Reload on a cross-tab change,
  // for the same reason switchTenant reloads locally: too much hoisted state is tenant-scoped to
  // reconcile piecemeal.
  useEffect(() => onActiveTenantChangedInAnotherTab(() => window.location.reload()), []);

  // ── "What's New" post-update view (?postupdate&version=…) ──
  // ENG-2724: release notes are now fetched (useWhatsNew), not statically imported, so the
  // ?postupdate lookup can't resolve synchronously on mount anymore — it resolves once
  // whatsNew.releases loads. Stays null (falls through to Home) unless ?postupdate is present
  // AND we have an authored release entry for that version.
  const [whatsNewRelease, setWhatsNewRelease] = useState<WhatsNewRelease | null>(null);
  useEffect(() => {
    if (whatsNew.loading) return;
    const p = new URLSearchParams(window.location.search);
    if (p.has('postupdate')) {
      setWhatsNewRelease(findReleaseForVersion(whatsNew.releases, p.get('version')));
    }
    // ENG-2722: `whatsNew.releases` is state inside useWhatsNew, so its identity changes only when
    // the fetch resolves — declaring it re-runs this lookup exactly once, when the list arrives.
  }, [whatsNew.loading, whatsNew.releases]);
  const dismissWhatsNew = () => {
    setWhatsNewRelease(null);
    const u = new URL(window.location.href);
    u.searchParams.delete('postupdate');
    u.searchParams.delete('version');
    replaceUrl(u.pathname + u.search + u.hash);
  };

  // ── Shared dialog state (used by App.tsx routing or both layouts) ──
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletedPlanInfo, setDeletedPlanInfo] = useState<DeletionReceipt | null>(null);

  // ── ENG-3479: one landing page per console ──
  // Home is Overview for an admin who can open it, and #billing is Settings outside the org console
  // (lib/consoleNav). The redirect REPLACES the entry it redirects from rather than pushing after it,
  // so Back never lands on a page that would only redirect again, and the page redirected away from is
  // never counted as a view. Nothing redirects until /subscription-status has answered; until then the
  // layout shows a spinner where those two pages would be, so an admin never sees Home flash first.
  const consoleKind = consoleKindFor(subscription.subscriptionStatus, subscription.subscriptionLoaded, subscription.subscriptionFailed);
  // ENG-3255: one `console_opened` per page load per signed-in account, for the Family Owner-return
  // metric. Not while this page is only serving the extension's sign-in.
  useConsoleOpenedEvent({
    uid: authHook.userInfo ? auth.currentUser?.uid ?? null : null,
    subscriptionLoaded: subscription.subscriptionLoaded,
    subscriptionStatus: subscription.subscriptionStatus,
    skip: extension.isExtensionMode,
  });
  // ENG-3454: read once, from the URL the person arrived on.
  const [signupIntent] = useState(() => readSignupIntent(window.location.search));
  // ENG-3628: the rarely shown full-page views are their own chunks; the one this URL can lead to
  // starts downloading now, alongside Firebase's auth check. Both inputs are read once at mount
  // (a Cancel can clear the MCP request, which re-runs this harmlessly: a repeated import is a no-op).
  // ENG-3832: a demo org claim link, from the URL this visit was opened with or saved before signing in.
  // Read once, at mount, before the URL writer below can normalize the hash away.
  const [claimToken, setClaimToken] = useState<string | null>(() => readClaimToken());
  const claimPending = claimToken !== null;
  const mcpAuthPending = mcpAuth.request !== null;
  useEffect(() => {
    prefetchRareViewsFor({ mcpAuth: mcpAuthPending, signupIntent, claimPending });
  }, [mcpAuthPending, signupIntent, claimPending]);
  // Saved so it survives signing in or up (the verification email opens a new tab). A sign-out clears the
  // saved copy (lib/userSession) while this tab keeps it, so it is saved again for whoever signs in next,
  // and never just because someone signed out.
  const claimUid = authHook.userInfo ? auth.currentUser?.uid ?? null : null;
  const claimSaved = useRef(false);
  useEffect(() => {
    if (!claimToken || (claimSaved.current && !claimUid)) return;
    saveClaimToken(claimToken);
    claimSaved.current = true;
  }, [claimToken, claimUid]);
  // public/index.html takes `#claim=` out of the address bar before any tag loads. Where it did not
  // run, the token must not sit in the URL for the page views below to record.
  useEffect(() => {
    if (claimTokenFromHash(window.location.hash)) replaceUrl(window.location.pathname + window.location.search);
  }, []);
  const claimShown = !!authHook.userInfo && claimPending;
  const signupStep = signupStepFor(signupIntent, { consoleKind, status: subscription.subscriptionStatus });
  const signupShown = !!authHook.userInfo && signupStep !== "none";
  // After the extension and desktop hand-offs have had their turn, and replacing this entry so Back
  // from the Web Store doesn't land here and redirect again.
  const webStoreRedirected = useRef(false);
  useEffect(() => {
    if (webStoreRedirected.current) return;
    if (authHook.userInfo && onboarding.onboardingChecked && !onboarding.isPostInstall && signupStep === "individual") {
      webStoreRedirected.current = true;
      trackSignupPlanSelected("individual", "url");
      trackIndividualToWebStore();
      window.location.replace(CHROME_STORE_URL);
    }
  }, [authHook.userInfo, onboarding.onboardingChecked, onboarding.isPostInstall, signupStep]);
  const gateFailed = subscription.subscriptionFailed;
  // ENG-4063: under protectedModel 2 "What a browser gets" and Policy → Sites & categories are gone;
  // their old links redirect, replacing the entry like a landing redirect (lib/protectedRouting).
  const model2Admin = deriveTenantAdmin(subscription.subscriptionStatus);
  const m2 = subscription.subscriptionLoaded
    ? model2Redirect(activePage, {
      model: protectedModelOf(subscription.subscriptionStatus?.protectedModel),
      kind: consoleKind,
      subject: effectiveSubject,
      policyTab,
      canOpenMember: model2Admin.capabilities.canManageMembers && model2Admin.capabilities.canViewMemberActivity,
    })
    : null;
  const m2Page = m2?.page ?? null;
  const m2UserId = m2?.userId ?? null;
  const m2Scope = m2?.sitesScope ?? null;
  const redirectTo = landingRedirect(activePage, subscription.subscriptionStatus, consoleKind, gateFailed)
    ?? (m2Page !== null && m2Page !== activePage ? m2Page : null);
  const pageDecided = landingDecided(activePage, consoleKind, gateFailed) && redirectTo === null;
  // Set by the redirect below, read and cleared by the URL writer when it writes the destination.
  const replaceNextUrl = useRef(false);
  useEffect(() => {
    if (!redirectTo) return;
    replaceNextUrl.current = true;
    setActivePage(redirectTo);
  }, [redirectTo]);
  useEffect(() => {
    if (!m2Page) return;
    replaceNextUrl.current = true;
    if (m2UserId) setSelectedUserId(m2UserId);
    if (m2Scope) setSitesScope(m2Scope);
    // `#policy?tab=sites` stays on Policy for a provider, on its default tab.
    if (m2Page === 'policy') setPolicyTab(null);
    setActivePage(m2Page);
  }, [m2Page, m2UserId, m2Scope]);
  // ENG-3614: when that redirect is because the viewer cannot open the page, the page they land on says
  // so. Held here, beside the redirect: it happens as soon as /subscription-status answers, which is
  // before DashboardLayout mounts, so a layout that watched for it never saw it (ENG-3526).
  const [accessNotice, dismissAccessNotice] = useAccessNotice(activePage, subscription.subscriptionStatus, consoleKind, auth.currentUser?.uid ?? null);
  // A navigation the console makes itself goes straight to where the page lives, so an admin's Home is
  // pushed as Overview rather than pushed as Home and then redirected.
  // ENG-3627: an admin who lands on Overview used to fetch its chunk only once the console had mounted
  // and redirected there. The moment /subscription-status says Overview is next is the moment the
  // landing redirect decides it, so the chunk (and the vendor chunks it needs) starts then, alongside
  // the console's own chunk (ENG-3620). Only for the org console's Overview, never for a member.
  const overviewIsNext = consoleKind === 'org' && (redirectTo ?? activePage) === 'overview'
    && deriveTenantAdmin(subscription.subscriptionStatus).capabilities.canViewDashboard;
  useEffect(() => {
    if (overviewIsNext) loadOverviewPage().catch(() => {});
  }, [overviewIsNext]);
  const navigateTo = (page: PageId) =>
    setActivePage(landingRedirect(page, subscription.subscriptionStatus, consoleKind, gateFailed) ?? page);

  // ── URL hash sync and page view tracking ──
  // ENG-2691: this used replaceState for every navigation, so the whole console occupied ONE history
  // entry no matter how many pages you visited — and the browser Back button therefore left the site
  // entirely, discarding the session's navigation. Moving between eleven pages and a drilldown, in a
  // tab you may have been in for an hour, with Back as an exit door, is not what anyone expects.
  //
  // ENG-3468: a Policy tab switch pushes too. It is a change of view a person makes on purpose, one
  // click at a time, and the console's convention since ENG-2691 is one entry per view; Back returning
  // to the previous tab is the point. Nothing switches tabs on its own, so this adds no entries anyone
  // did not ask for.
  useEffect(() => {
    // ENG-3479: nothing is written for a page about to redirect. Written, it would match the URL (Back
    // to `` shows ``), clear the replace flag, and the destination would then be PUSHED — dropping the
    // Forward history and leaving Back to land on `` and be pushed forward again, for ever. Skipped, the
    // destination's own write replaces the entry the redirect left.
    // ENG-3454: the signup screen stands in front of the console, which must not write its page meanwhile.
    // ENG-3832: so does the claim screen.
    if (redirectTo || signupShown || claimShown) return;
    const hash = hashForPage(activePage, selectedUserId, policyTab, effectiveSubject);
    const url = window.location.pathname + window.location.search + hash;
    if (!historyPrimed.current) {
      // The first run only NORMALIZES the URL the app was opened with (`#home` → ``, an unknown hash
      // → ``). The browser already made an entry for that load, so pushing here would leave a
      // duplicate that Back appears to do nothing on. It stamps that entry's position even when the
      // URL needs nothing (ENG-3376).
      window.history.replaceState(withHistoryIndex(window.history.state, historyIndex.current), '', url);
    } else if (window.location.hash !== hash) {
      if (replaceNextUrl.current) {
        // ENG-3479: a landing redirect takes the place of the entry it redirected from.
        window.history.replaceState(withHistoryIndex(window.history.state, historyIndex.current), '', url);
      } else {
        historyIndex.current += 1;
        window.history.pushState(withHistoryIndex(null, historyIndex.current), '', url);
      }
    }
    replaceNextUrl.current = false;
    historyPrimed.current = true;
  }, [activePage, selectedUserId, policyTab, effectiveSubject, redirectTo, signupShown, claimShown]);

  // Track page view for SPA navigation. Apart from the URL sync above since ENG-3468, so a Policy tab
  // switch, which changes the URL, is not counted as another view of the page.
  //
  // ENG-3479: counted once, for the page actually shown. Signed in, that waits for a page that is about
  // to redirect or whose destination is not known yet (Home and Billing before /subscription-status
  // answers); `pageDecided` is always true for every other page, so for them nothing changes. Signed
  // out there is no status to wait for, and the sign-in screen is counted as Home once Firebase has
  // said nobody is signed in — before that, the page is a spinner that may yet become Overview.
  const viewDecided = authHook.userInfo !== null ? pageDecided : authHook.authInitialized;
  useEffect(() => {
    if (!viewDecided) return;
    trackPageView(`Account - ${activePage}`, {
      page_path: `/${activePage}`,
      user_tier: subscription.subscriptionStatus?.plan || 'free',
    });
  }, [activePage, selectedUserId, subscription.subscriptionStatus?.plan, viewDecided]);

  // ── Send auth state to desktop app via deep link ──
  // Fires when isDesktopMode, auth state (userInfo), or subscription status changes.
  // A ref guards against double-firing: once the redirect is sent we don't send again
  // (avoids a second attempt when subscriptionStatus loads after the first fire, which
  // browsers often block silently on repeated custom-protocol navigations).
  const desktopRedirectSent = React.useRef(false);
  // ENG-2576: an install happens once, so its event must be reported once per load.
  const postInstallTracked = React.useRef(false);
  useEffect(() => {
    if (!isDesktopMode) return;
    if (desktopRedirectSent.current) return;
    const user = auth.currentUser;
    if (!user) return;

    desktopRedirectSent.current = true;

    user.getIdToken().then((idToken) => {
      const userData = {
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        uid: user.uid
      };

      // Track desktop app login
      const plan = (subscription.subscriptionStatus?.plan || 'free') as 'free' | 'premium';
      trackDesktopAppLogin(plan);

      // Use local HTTP callback in dev (callbackUrl param), haven:// in prod.
      const HAVEN_AUTH_COMPLETE_URL = 'haven://auth-complete';
      const base = desktopCallbackUrl ?? HAVEN_AUTH_COMPLETE_URL;
      const callbackUrl = new URL(base);
      callbackUrl.searchParams.set('idToken', idToken);
      callbackUrl.searchParams.set('refreshToken', user.refreshToken);
      callbackUrl.searchParams.set('user', encodeURIComponent(JSON.stringify(userData)));
      if (subscription.subscriptionStatus) {
        callbackUrl.searchParams.set('subscriptionPlan', encodeURIComponent(JSON.stringify(subscription.subscriptionStatus)));
      }

      console.log('Redirecting to desktop app with auth data via', desktopCallbackUrl ? 'local HTTP' : 'haven://');
      window.location.href = callbackUrl.toString();
    }).catch((error) => {
      console.error('Failed to get ID token for desktop app:', error);
      desktopRedirectSent.current = false; // allow retry on token error
    });
    // ENG-2722: the suppression here was stale — the declared list already satisfies the rule.
    // Everything else this reads is a ref or a module-level import.
  }, [isDesktopMode, desktopCallbackUrl, authHook.userInfo, subscription.subscriptionStatus]);

  // ── The loads that wait on /subscription-status ──
  //
  // They depend on the role it carries. Run once per sign-in: by the auth listener below when the
  // first answer arrives, or, when that answer failed, by the effect after this once a later one does
  // (ENG-3350). Before that, a failure at sign-in left them unstarted for the session: the custom
  // sites spun, the roster never loaded, and the nav's Try again refetched only the subscription.
  const launchExtensionIdRef = useRef<string | null>(null);
  const subscriptionLoadsPendingRef = useRef(false);
  const loadAfterSubscription = (subData: SubscriptionStatus) => {
    subscriptionLoadsPendingRef.current = false;
    // Personal custom sites (managed via the extension) are available to everyone.
    sites.fetchCustomSites(() => launchExtensionIdRef.current || extension.detectedExtensionId || readStored('havenExtensionId'));
    // Team data is B2B-only — only fetch it for actual tenant members (the server
    // returns a role). This avoids 404s on /business/* for free/solo users.
    if (subData.role) {
      // ENG-3621: the roster only for a role that opens the organization's console — an owner or
      // admin, or an IT provider's operator in a client. `GET /tenant/members` is admin-only and
      // answered every member with a 404, and every page that reads the roster (Members & seats, a
      // member's page, Groups, Overview, What a browser gets, Risk report) is one only those roles
      // reach. The one exception, Settings' deletion check, loads it on arrival below.
      if (isConsoleRole(subData.role)) team.fetchBusinessMembers();
      // ENG-3894: a family has no organization custom sites (the backend refuses them). ENG-3897: its
      // people keep their own instead, as someone in no organization does.
      if (subData.tenantType === 'family') {
        sites.fetchMyCustomSites();
      } else {
        team.fetchBusinessCustomSites();
      }
      if (subData.businessCustomSites) {
        team.setBusinessCustomSites(subData.businessCustomSites);
      }
      // ENG-2571: the admin-page payloads used to be prefetched right here, on the login
      // path — the whitelist pair, the BigQuery-backed /business/dashboard, the three
      // org-settings calls and groups, all fired before the admin had chosen a page, and
      // usually for pages they never opened. They now load when their page mounts, which is
      // what Risk Report and telemetry-events already did. The hooks are still hoisted here,
      // so their state survives navigation and a second visit is instant; each fetch is
      // guarded by its own loaded flag, so mounting twice does not re-request.
    } else {
      // Free/solo users (no team) manage their own server-backed custom sites.
      sites.fetchMyCustomSites();
    }
  };
  const loadAfterSubscriptionRef = useRef(loadAfterSubscription);
  loadAfterSubscriptionRef.current = loadAfterSubscription;
  useEffect(() => {
    const status = subscription.subscriptionStatus;
    if (status && subscriptionLoadsPendingRef.current) loadAfterSubscriptionRef.current(status);
  }, [subscription.subscriptionStatus]);

  // ── The loads that wait for the page that shows them (ENG-3621) ──
  // The 30s poll of /my-categories ran on every page for the whole session, for a selection that only
  // Protected sites and Policy read. It now runs while one of them is open, and starting it reads at
  // once, so every arrival shows the selection as it is now rather than as it was at sign-in. /banks
  // feeds Protected sites' list of the apps the organization tracks and nothing else, so it waits for
  // that page; on arrival only, through a ref, so a failed load is retried on the next visit rather
  // than on every render. Signing out clears `userInfo`, which stops the poll.
  const signedIn = authHook.userInfo !== null;
  const showsSelection = signedIn && PAGES_SHOWING_SELECTION.has(activePage);
  const showsTrackedApps = signedIn && activePage === 'sites';
  const { startCategoriesPolling, stopCategoriesPolling, loadBanksOnce } = sites;
  useEffect(() => {
    if (showsSelection) startCategoriesPolling();
    else stopCategoriesPolling();
  }, [showsSelection, startCategoriesPolling, stopCategoriesPolling]);
  const loadBanksOnceRef = useRef(loadBanksOnce);
  loadBanksOnceRef.current = loadBanksOnce;
  useEffect(() => {
    if (showsTrackedApps) loadBanksOnceRef.current();
  }, [showsTrackedApps]);
  // A plain member of the org /subscription-status names can still own another org, which
  // `GET /tenant/members` (no header) answers with. Settings' deletion check reads that roster to
  // refuse up front, before the exit survey and the Gmail disconnect that run ahead of the server's
  // own 409, so a member's arrival on Settings loads it rather than every sign-in.
  const memberRole = subscription.subscriptionStatus?.role;
  const needsDeletionRoster = signedIn && activePage === 'settings'
    && !!memberRole && !isConsoleRole(memberRole) && !team.membersLoaded;
  const fetchBusinessMembersRef = useRef(team.fetchBusinessMembers);
  fetchBusinessMembersRef.current = team.fetchBusinessMembers;
  useEffect(() => {
    if (needsDeletionRoster) fetchBusinessMembersRef.current();
  }, [needsDeletionRoster]);

  // ── Main init useEffect: URL params, extension detection, auth listener ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const extId = params.get('extensionId');
    launchExtensionIdRef.current = extId;
    const desktopApp = params.get('desktopApp');
    const logout = params.get('logout');

    // ENG-2619: managed (force-installed) extension session — hide sign-out here. Persist so it
    // survives the param strip + in-session reloads; the extension re-sends ?managed=1 each open.
    if (params.get('managed') === '1') {
      setManaged(true);
      try { sessionStorage.setItem('havenManaged', '1'); } catch { /* sessionStorage unavailable */ }
    }

    // ENG-2576: the extension opens this app at `?postinstall&extensionId=…&utm_*` for every new
    // install — it is the ONLY moment we learn an install completed — and nothing read the param.
    //
    // `trackExtensionPostInstallPageViewed` was written for exactly this and sat with zero callers:
    // the post-install ONBOARDING flow was removed (useOnboarding's updateIsPostInstall is now a
    // no-op) and whatever invoked the tracker went with it, leaving the landing itself untracked.
    //
    // The extension does its own work to make this attributable — it reads the haven_attribution
    // cookie the marketing site wrote and forwards it as utm_source/medium/campaign on this URL,
    // because the Chrome Web Store strips params in between. That chain ended here, unread.
    //
    // Its own `extension_installed` event still reaches Haven's BigQuery, so installs were never
    // invisible to US. What was missing is the GA4/Google Ads side: automatic page_view carries the
    // UTM for SESSION attribution, but with no install EVENT there is nothing to count or to mark as
    // a key event — so the ads funnel ended at click_chrome_webstore, which is intent, not install.
    //
    // Ref-guarded like desktopRedirectSent: an install is one occurrence, and a re-render or a
    // StrictMode double-effect must not report two.
    if (params.has('postinstall') && !postInstallTracked.current) {
      postInstallTracked.current = true;
      trackExtensionPostInstallPageViewed();
    }
    const paymentSuccess = params.get('payment');
    // Teams page source: pre-fill email and track source for onboarding. ENG-3430: the email is not in
    // the URL any more; public/index.html took it out before GA4 or Google Ads could record it.
    const sourceParam = params.get('source');
    const emailParam = readUrlSecret('email');
    if (sourceParam) {
      authHook.setIsSignUp(true);
    }
    if (emailParam) {
      authHook.setEmail(emailParam);
    }

    // Desktop app mode detection
    if (desktopApp === 'true') {
      setIsDesktopMode(true);
      const callbackUrl = params.get('callbackUrl');
      if (callbackUrl && isSafeDesktopCallback(callbackUrl, window.location.hostname)) {
        setDesktopCallbackUrl(callbackUrl);
      }
    }

    // ENG-4072: KEPT, though the extension's popup no longer opens this URL (its Log out ends only the
    // extension's session). Extension versions already in the field still open `?logout=true` on Log
    // out and wait for `logout-complete` from here, and the desktop app's Log out opens it in the
    // system browser (haven-consumer-desktop-app, `ipcMain.handle("logout")`) to sign the website out.
    // Remove this handler and `isLoggingOutRef` once old extension versions are gone and the desktop
    // app has its own way to end the website session.
    if (logout === 'true') {
      isLoggingOutRef.current = true;
      // Clear the logout param so subsequent logins aren't blocked
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('logout');
      replaceUrl(cleanUrl.pathname + cleanUrl.search);
      auth.signOut().then(() => {
        authHook.setUserInfo(null);
      }).catch(() => {
        // signOut failed, continue to auth listener anyway
      });
    }

    const planParam = params.get('plan');
    // Only the legacy checkout returns `premium`; `family` and `business` come from the tenant checkout (ENG-3181).
    if (paymentSuccess === 'success' && planParam === 'premium') {
      team.setBusinessSuccess('Your Haven Premium subscription is active! You can now invite members.');
      setActivePage('users');
      subscription.fetchSubscriptionStatus(); // Bug #13: Refresh subscription after checkout

      // Track purchase
      const seats = 1;
      const value = calculateAnnualLTV('premium', seats, true);
      trackPurchase('premium', value, 'yearly', seats);
    } else if (paymentSuccess === 'success' && (planParam === 'family' || planParam === 'business')) {
      team.setBusinessSuccess(planParam === 'family' ? 'Haven Family is on. Invite your family next.' : 'Haven Business is on. Invite your members next.');
      setActivePage('users');
      // Stripe redirects before its webhook licenses the org, so the licence can land a moment later.
      void (async () => {
        for (let attempt = 0; attempt < 10; attempt++) {
          const status = await subscription.fetchSubscriptionStatus();
          if (status?.hasSubscription && status.status === 'active') return;
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      })();
    }

    joinInvitation.initJoinToken(onboarding.updateIsPostInstall);
    extension.initFromParams(extId);

    window.addEventListener("haven-extension-detected", extension.handleExtensionDetected);
    extension.checkAlreadyDetected();

    // ENG-3406: everything this tab holds for the account that just ended, run on every sign-out and
    // every change of account. Every way an account ends reaches the listener below, which is why this
    // lives here and not in Log Out: a sign-out from the extension (`?logout=true`), an account deletion
    // and a sign-out in another tab never went through useAuth.handleLogout. It also runs when Firebase
    // reports a different account with no signed-out answer in between.
    //
    // `extensionSubscription` matters most. It is what the sign-in handlers below used to hand the
    // extension, and the extension stamps whatever plan it is handed with the account signing in, so
    // the next person's extension served the previous person's organization (roster, allow and deny
    // lists, locked sites, enforcement) as its own for five minutes, and kept it if their own status
    // then failed to load. The handlers now send no plan at all, and resetting it here keeps any later
    // push (the 45-minute refresh, a newly detected extension) from sending the previous account's.
    //
    // The hooks' fetches are fire-and-forget and some are not cancellable; the generation bump makes
    // this listener's own in-flight work for the previous account drop its result (the `gen` checks
    // below), and useSubscription.reset does the same for /subscription-status.
    const resetPerUserState = () => {
      ++onboarding.authGenerationRef.current;
      subscriptionLoadsPendingRef.current = false;
      subscription.reset();
      setExtensionSubscription(null);
      sites.reset();
      team.reset();
      overview.reset();
      riskReport.reset();
      saasInventory.reset();
      home.reset();
      whitelist.reset();
      orgSettings.reset();
      groups.reset();
      mspAccess.reset();
      directory.reset();
      joinInvitation.resetForSignOut();
      setActivePage('home');
      setSelectedUserId(null);
      // ENG-3614 / ENG-3652: the access notice is that account's alone. useAccessNotice now clears it
      // itself on a uid change (passed in below), so nothing here needs to remember to call
      // dismissAccessNotice() — the explicit call used to live here and missed the case where the
      // next account also landed on the page the notice was already showing (Home, most commonly).
      clearPerUserStorage();
      onboarding.resetOnboarding();
    };

    let lastUid: string | null = null;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // A different account in the same tab, with no signed-out answer in between.
        if (lastUid && lastUid !== user.uid) {
          resetPerUserState();
        }
        lastUid = user.uid;
        // ENG-3620: the console's chunk, alongside the requests below rather than after them. A failure
        // is the lazy component's to report, when it asks again on render.
        loadDashboard().catch(() => {});

        const gen = ++onboarding.authGenerationRef.current;
        onboarding.setOnboardingChecked(false);
        authHook.setJustLoggedOut(false);
        authHook.setUserInfo({
          name: user.displayName || "User",
          email: user.email || "N/A",
          photoURL: user.photoURL || null,
          // ENG-3413: never a guessed 'password' — Settings offers a Password card on this alone.
          provider: accountProvider(user.providerData),
        });

        // These do not depend on subscription state, so they start BEFORE the await rather
        // than behind it. They used to sit after `/subscription-status` resolved, which made the
        // whole signed-in first paint wait on one request that none of them need — cheap when the
        // backend is warm (p50 ~35ms) and expensive exactly when it is not: during the 2026-08-13
        // saturation episode /my-categories and its siblings ran 5-20s, and this shape added the
        // subscription call's latency on top of every one of them.
        //
        // ⚠ This widens an existing race rather than introducing one. All of them are fire-and-forget
        // and nothing cancels them, so an auth change mid-flight could already land the previous
        // user's data — the `gen` check below only ever narrowed that window, it never closed it.
        // Starting them earlier widens it by roughly the duration of the subscription call. The fix
        // for the race is a generation check inside the hooks' own setState, which is a separate
        // change; this does not make that harder.
        // ENG-3621: not /banks, and not the 30s poll of /my-categories; both wait for a page that
        // shows them (the effect after this one).
        sites.fetchCategories();
        sites.fetchEnabledCategories();
        // Home dashboard metrics (self-scoped on the backend). Fire-and-forget — Home shows its own
        // loading state meanwhile, and "Couldn't load your activity" with Try again if this fails
        // (ENG-3350; it used to degrade to the new-user empty state).
        home.fetchHomeMetrics();

        const consoleStatus = subscription.fetchSubscriptionStatus().then((subData) => {
          // ENG-3350: a failure holds the loads that depend on the role until a later
          // /subscription-status answers (the nav's Try again, say); nothing else would start them, so
          // the custom sites spun for good and the roster never loaded. Set HERE, as this answer
          // lands, not once the extension's status below has too: with a tenant override that is a
          // request of its own, and a Try again that answered during it would find nothing pending
          // and start nothing.
          if (!subData && gen === onboarding.authGenerationRef.current) subscriptionLoadsPendingRef.current = true;
          return subData;
        });

        // ENG-2387: resolve the extension-bound status before anything can push it.
        // Without an active switch this is the console's status and costs no extra request. ENG-3461:
        // stamped with the account it was fetched for, which the `gen` checks keep equal to `user`.
        // ENG-3621: with a switch, it is the operator's own unscoped /subscription-status, which does
        // not need the console's answer, so the two requests go out together rather than one after
        // the other. Neither promise rejects (both answer null on a failure).
        const fetchedFor = { uid: user.uid, email: user.email };
        const [subData, ownSubData] = await Promise.all([
          consoleStatus,
          isTenantOverridden()
            ? resolveExtensionSubscription(null, fetchedFor)
            : consoleStatus.then((status) => resolveExtensionSubscription(status, fetchedFor)),
        ]);
        if (gen !== onboarding.authGenerationRef.current) return;
        setExtensionSubscription(ownSubData);

        // Attribute the install to a referral sharer if the website /invite page
        // left a `haven_ref` cookie on `.starthaven.com`. Fire-and-forget — it
        // swallows all errors and must never block the auth flow.
        captureReferral();

        // Set GA4 user properties with enhanced tracking
        const plan = (subData?.plan || 'free') as 'free' | 'premium';
        const seats = subData?.totalSeats || 1;
        const annualLtv = calculateAnnualLTV(plan, seats, true);

        setUserProperties({
          user_tier: plan,
          user_ltv: annualLtv,
          signup_date: user.metadata?.creationTime || new Date().toISOString(),
          has_extension: extension.extensionInstalled === true,
          has_desktop_app: isDesktopMode,
        });

        if (subData) loadAfterSubscriptionRef.current(subData);

        // Notify all extensions of auth state
        const allIds = extension.getAllExtensionIds();
        if (allIds.length > 0 && !isLoggingOutRef.current) {
          try {
            const idToken = await user.getIdToken();
            if (gen !== onboarding.authGenerationRef.current) return;

            extension.sendToAllExtensions({
              action: "auth-complete",
              idToken,
              refreshToken: user.refreshToken,
              user: { displayName: user.displayName, email: user.email, photoURL: user.photoURL, uid: user.uid },
              _tokenResponse: { idToken },
              // ENG-2387: the operator's OWN tenant, not the one selected in the console.
              subscriptionPlan: ownSubData || null,
              // ENG-4072: explicit only for a sign-in the person just did; a page load is passive.
              explicit: Date.now() - explicitLoginAtRef.current < EXPLICIT_LOGIN_WINDOW_MS,
            });
          } catch {
            // Extension not available
          }
        }

        // Post-install onboarding has been removed — after auth, users go straight to the
        // dashboard (the new-user state shows the onboarding carousel there). No survey-status
        // gating, install/pin wizard, or tutorial in between.
        onboarding.setOnboardingChecked(true);
      } else {
        if (isLoggingOutRef.current) {
          // An extension-initiated logout finished signing out — tell every detected
          // extension to fully clear its stored auth (background runs full teardown).
          // ENG-4070: isLoggingOutRef is set only by the extension's `?logout=true` hand-off.
          extension.sendToAllExtensions({ action: "logout-complete", source: "popup" satisfies LogoutSource });
          isLoggingOutRef.current = false;
        }
        if (lastUid) {
          // Real logout / user-switch — clear per-account state so it can't leak to the
          // next user who signs in in this same tab. (inviteContext is intentionally left
          // alone so the join-via-link flow for logged-out users isn't disrupted.)
          resetPerUserState();
          lastUid = null;
        }
        authHook.setUserInfo(null);
        subscription.reset();
        onboarding.setOnboardingChecked(false);
        onboarding.resetOnboarding();
      }
      authHook.setAuthInitialized(true);
    });

    return () => {
      unsubscribe();
      window.removeEventListener("haven-extension-detected", extension.handleExtensionDetected);
    };
    // ENG-2722: KEPT DELIBERATELY, and this is the one place in the app where that is the right
    // answer. The effect subscribes to `onAuthStateChanged` and registers a DOM listener exactly
    // once per mount; re-running it would tear down and re-establish the app's auth subscription.
    // The omitted names are the hook objects the callback reaches into, so the alternative is a ref
    // holding all of them — mechanical, but it rewrites the auth path for one suppression, and this
    // effect must not re-run whatever their identities do. Declaring them is NOT an option.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cross-domain handlers ──
  const handleGoogleLogin = async () => {
    explicitLoginAtRef.current = Date.now();
    const result = await authHook.handleGoogleLogin();
    if (!result) explicitLoginAtRef.current = 0;
    if (result && extension.isExtensionMode) {
      extension.sendToAllExtensions({
        action: "auth-complete",
        // ENG-4072: the person just signed in here. A confirmed Log out in the extension ignores the
        // site's passive pushes (a page load, the 45-minute refresh) but not this one.
        explicit: true,
        idToken: result.idToken,
        refreshToken: result.user.refreshToken,
        user: { displayName: result.user.displayName, email: result.user.email, photoURL: result.user.photoURL, uid: result.user.uid },
        _tokenResponse: { idToken: result.idToken },
        // ENG-3406: never a plan. The one this render holds can be the PREVIOUS account's, and the
        // extension stamps any plan it is given with the account signing in. This account's own
        // (ENG-2387) follows in the subscription-updated push once it has loaded; until then the
        // extension fetches its own.
        subscriptionPlan: null,
      });
    } else if (!result && extension.isExtensionMode) {
      extension.sendToAllExtensions({ action: "auth-failed" });
    }
  };

  const handleMicrosoftLogin = async () => {
    explicitLoginAtRef.current = Date.now();
    const result = await authHook.handleMicrosoftLogin();
    if (!result) explicitLoginAtRef.current = 0;
    if (result && extension.isExtensionMode) {
      extension.sendToAllExtensions({
        action: "auth-complete",
        // ENG-4072: the person just signed in here. A confirmed Log out in the extension ignores the
        // site's passive pushes (a page load, the 45-minute refresh) but not this one.
        explicit: true,
        idToken: result.idToken,
        refreshToken: result.user.refreshToken,
        user: { displayName: result.user.displayName, email: result.user.email, photoURL: result.user.photoURL, uid: result.user.uid },
        _tokenResponse: { idToken: result.idToken },
        // ENG-3406: never a plan. The one this render holds can be the PREVIOUS account's, and the
        // extension stamps any plan it is given with the account signing in. This account's own
        // (ENG-2387) follows in the subscription-updated push once it has loaded; until then the
        // extension fetches its own.
        subscriptionPlan: null,
      });
    } else if (!result && extension.isExtensionMode) {
      extension.sendToAllExtensions({ action: "auth-failed" });
    }
  };

  const handleAppleLogin = async () => {
    explicitLoginAtRef.current = Date.now();
    const result = await authHook.handleAppleLogin();
    if (!result) explicitLoginAtRef.current = 0;
    if (result && extension.isExtensionMode) {
      extension.sendToAllExtensions({
        action: "auth-complete",
        // ENG-4072: the person just signed in here. A confirmed Log out in the extension ignores the
        // site's passive pushes (a page load, the 45-minute refresh) but not this one.
        explicit: true,
        idToken: result.idToken,
        refreshToken: result.user.refreshToken,
        user: { displayName: result.user.displayName, email: result.user.email, photoURL: result.user.photoURL, uid: result.user.uid },
        _tokenResponse: { idToken: result.idToken },
        // ENG-3406: never a plan. The one this render holds can be the PREVIOUS account's, and the
        // extension stamps any plan it is given with the account signing in. This account's own
        // (ENG-2387) follows in the subscription-updated push once it has loaded; until then the
        // extension fetches its own.
        subscriptionPlan: null,
      });
    } else if (!result && extension.isExtensionMode) {
      extension.sendToAllExtensions({ action: "auth-failed" });
    }
  };

  const handleEmailAuth = async () => {
    explicitLoginAtRef.current = Date.now();
    const result = await authHook.handleEmailAuth();
    if (!result) explicitLoginAtRef.current = 0;
    if (result && extension.isExtensionMode) {
      extension.sendToAllExtensions({
        action: "auth-complete",
        // ENG-4072: the person just signed in here. A confirmed Log out in the extension ignores the
        // site's passive pushes (a page load, the 45-minute refresh) but not this one.
        explicit: true,
        idToken: result.idToken,
        refreshToken: result.user.refreshToken,
        user: { displayName: result.user.displayName, email: result.user.email, photoURL: result.user.photoURL, uid: result.user.uid },
        _tokenResponse: { idToken: result.idToken },
        // ENG-3406: never a plan. The one this render holds can be the PREVIOUS account's, and the
        // extension stamps any plan it is given with the account signing in. This account's own
        // (ENG-2387) follows in the subscription-updated push once it has loaded; until then the
        // extension fetches its own.
        subscriptionPlan: null,
      });
    } else if (!result && extension.isExtensionMode) {
      extension.sendToAllExtensions({ action: "auth-failed" });
    }
  };

  const handleLogout = () => authHook.handleLogout(extension.getExtensionId, extension.getAllExtensionIds);

  // ── Loading state ──
  if (!authHook.authInitialized) {
    return <FullPageSpinner />;
  }

  // ── Password reset page ──
  // ENG-3429: the code is no longer in the URL by the time this runs — public/index.html moved it out
  // before any analytics tag could record it. passwordResetCode() reads it from where it went, and is
  // null after a reload, which the page answers with how to get back to the form.
  if (isPasswordResetLink()) {
    return <Suspense fallback={<FullPageSpinner />}><ResetPasswordPage oobCode={passwordResetCode()} /></Suspense>;
  }

  // ── Email verification page (ENG-4080) ──
  // The code is out of the URL like the reset code; a reload has none and the page says to reopen the link.
  if (isVerifyEmailLink()) {
    return <Suspense fallback={<FullPageSpinner />}><VerifyEmailPage oobCode={verifyEmailCode()} /></Suspense>;
  }

  // ── Confirm email alias page (token from the emailed link) ──
  // ENG-3430: out of the URL before any analytics tag loads, like the reset code above.
  const confirmAliasToken = readUrlSecret('confirmAlias');
  if (confirmAliasToken) {
    return <Suspense fallback={<FullPageSpinner />}><ConfirmAliasPage token={confirmAliasToken} /></Suspense>;
  }

  // ── Connect Claude (account-mcp OAuth handoff, ENG-2209) ──
  // Signed out, we fall through to the login view; after signing in the customer lands back on this
  // consent screen. parseMcpAuthRequest returns null unless the callback is an allow-listed
  // account-mcp origin — a forged link never reaches the consent screen.
  //
  // The request comes from INITIAL_HASH, not the live hash (useMcpAuthRequest), and MUST keep doing
  // so: the hash-sync effect above normalizes `/mcp-auth` to `home` (empty hash) on the first commit,
  // which happens BEFORE Firebase auth hydrates and populates userInfo. Reading live, this branch could
  // never fire — the request was already erased by the time the condition became true.
  //
  // ENG-3348: which is also why Cancel ends the flow through `mcpAuth.cancel` rather than the URL.
  // The console renders in its place, already on Home: `/mcp-auth` is not a PageId.
  if (mcpAuth.request && authHook.userInfo) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <McpAuthPage request={mcpAuth.request} userEmail={authHook.userInfo.email} onCancel={mcpAuth.cancel} />
      </Suspense>
    );
  }

  // ── Account deleted confirmation page ──
  if (showDeleteConfirmation && deletedPlanInfo) {
    return (
      <AccountDeletedPage
        deletedPlanInfo={deletedPlanInfo}
        isDark={isDark}
        theme={theme}
        onReturnToLogin={() => { setShowDeleteConfirmation(false); setDeletedPlanInfo(null); }}
      />
    );
  }

  // ── Wait for onboarding check before showing dashboard ──
  if (authHook.userInfo && !onboarding.onboardingChecked && !onboarding.isPostInstall) {
    return <FullPageSpinner />;
  }

  // ── "What's New" view (logged in, opened via ?postupdate) ──
  // Takes precedence over the dashboard and dismisses to Home. Signed-out users never reach
  // here (they fall through to the login view below), so we never force a login wall just to
  // show a changelog.
  if (authHook.userInfo && onboarding.onboardingChecked && !onboarding.isPostInstall && whatsNewRelease) {
    return <Suspense fallback={<FullPageSpinner />}><WhatsNewPage release={whatsNewRelease} onDismiss={dismissWhatsNew} /></Suspense>;
  }

  // ── A demo org claim link (ENG-3832): ahead of signup and the console ──
  if (authHook.userInfo && onboarding.onboardingChecked && !onboarding.isPostInstall && claimToken) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <ClaimOrgFlow
          token={claimToken}
          email={authHook.userInfo.email}
          onOpenOrg={(tenantId) => {
            // Opened as a switch does it, like a new signup: pin the organization and reload, so the
            // organization list and every hook read it fresh, as its owner.
            forgetClaimToken();
            setActiveTenantId(tenantId);
            replaceUrl(window.location.pathname + window.location.search);
            window.location.reload();
          }}
          onDismiss={() => {
            forgetClaimToken();
            setClaimToken(null);
          }}
          onSwitchAccount={handleLogout}
        />
      </Suspense>
    );
  }

  // ── Signup: the plan step and naming the organization (ENG-3454) ──
  if (authHook.userInfo && onboarding.onboardingChecked && !onboarding.isPostInstall && signupStep !== "none") {
    if (signupStep === "plan" || signupStep === "create" || signupStep === "checkout") {
      return (
        <Suspense fallback={<FullPageSpinner />}>
          <SignupFlow
            initialPlan={signupStep !== "plan" && signupIntent?.plan && signupIntent.plan !== "individual" ? signupIntent.plan : null}
            resumeTenantId={signupStep === "checkout" ? subscription.subscriptionStatus?.tenantId : undefined}
            source={signupIntent?.source}
            onJustMe={() => {
              trackIndividualToWebStore();
              window.location.assign(CHROME_STORE_URL);
            }}
            onFinished={(tenantId) => {
              // Opened as a switch does it: pin the organization and reload, so every hook reads it fresh.
              setActiveTenantId(tenantId);
              replaceUrl(withoutSignupParams(window.location.href));
              window.location.reload();
            }}
          />
        </Suspense>
      );
    }
    return <FullPageSpinner />;
  }

  // ── Dashboard layout (logged in) ──
  if (authHook.userInfo && onboarding.onboardingChecked && !onboarding.isPostInstall) {
    return (
      <Suspense fallback={<FullPageSpinner />}>
        <DashboardLayout
          // ENG-3406: a new account gets a new layout. DashboardLayout holds per-account hooks of its own
          // (the tenant switcher's list, the Managed access badge, the extension inventory), which a
          // sign-out clears by unmounting it. A switch straight to another account does not unmount it.
          key={auth.currentUser?.uid}
          theme={theme}
          isDark={isDark}
          userInfo={authHook.userInfo}
          error={authHook.error}
          onSetError={authHook.setError}
          onSetUserInfo={authHook.setUserInfo}
          // ENG-3413: the change signs out every other session, including the extension here, so the
          // new tokens go on to every extension this page has found. The operator's OWN status (ENG-2387).
          onChangePassword={(currentPassword, newPassword) => authHook.handleChangePassword(currentPassword, newPassword, {
            ids: extension.getAllExtensionIds(),
            subscriptionPlan: extensionSubscription,
          })}
          onForgotPassword={() => authHook.sendPasswordResetEmail(authHook.userInfo!.email)}
          activePage={activePage}
          onSetActivePage={navigateTo}
          selectedUserId={selectedUserId}
          onOpenUserDetail={openUserDetail}
          policyTab={policyTab}
          onSetPolicyTab={setPolicyTab}
          effectiveSubject={effectiveSubject}
          onSetEffectiveSubject={setEffectiveSubject}
          onOpenEffective={openEffective}
          sitesScope={sitesScope}
          accessNotice={accessNotice}
          onDismissAccessNotice={dismissAccessNotice}
          onToggleMode={toggleMode}
          onLogout={handleLogout}
          managed={managed}
          // ENG-2682: the operator's OWN status, so Secure Sites can show the policy that actually
          // governs this browser rather than the tenant the console happens to be viewing.
          ownSubscriptionStatus={extensionSubscription}
          extensionInstalled={extension.extensionInstalled}
          getExtensionId={extension.getExtensionId}
          getAllExtensionIds={extension.getAllExtensionIds}
          onAccountDeleted={(planInfo) => { setDeletedPlanInfo(planInfo); setShowDeleteConfirmation(true); }}
          subscription={subscription}
          homeMetrics={home.homeMetrics}
          whatsNewReleases={whatsNew.releases}
          homeMetricsStatus={home.homeMetricsStatus}
          emailUnverified={home.emailUnverified}
          onRetryHomeMetrics={() => void home.fetchHomeMetrics()}
          sites={sites}
          team={team}
          whitelist={whitelist}
          overview={overview}
          riskReport={riskReport}
          saasInventory={saasInventory}
          orgSettings={orgSettings}
          groups={groups}
          mspAccess={mspAccess}
          directory={directory}
          joinInvitation={{
            joinResult: joinInvitation.joinResult,
            setJoinResult: joinInvitation.setJoinResult,
            joinConfirmDialogOpen: joinInvitation.joinConfirmDialogOpen,
            inviteContext: joinInvitation.inviteContext,
            handleConfirmJoinBusiness: joinInvitation.handleConfirmJoinBusiness,
            handleCancelJoinBusiness: joinInvitation.handleCancelJoinBusiness,
          }}
        />
      </Suspense>
    );
  }

  // ── Signed-out view: the redesigned brand auth screen ──
  return (
    <AuthScreen
      error={authHook.error}
      isSignUp={authHook.isSignUp}
      firstName={authHook.firstName}
      lastName={authHook.lastName}
      email={authHook.email}
      password={authHook.password}
      loading={authHook.loading}
      onFirstNameChange={authHook.setFirstName}
      onLastNameChange={authHook.setLastName}
      onEmailChange={authHook.setEmail}
      onPasswordChange={authHook.setPassword}
      onSubmit={handleEmailAuth}
      onGoogleLogin={handleGoogleLogin}
      onMicrosoftLogin={handleMicrosoftLogin}
      onAppleLogin={handleAppleLogin}
      onProviderIntent={warmUpPopupResolver}
      onToggleSignUp={authHook.toggleSignUp}
      onForgotPassword={authHook.handleForgotPassword}
      onClearError={() => authHook.setError(null)}
      showForgotPassword={onboarding.showForgotPassword}
      onForgotPasswordToggle={onboarding.setShowForgotPassword}
      inviteContext={joinInvitation.inviteContext}
      claimPending={claimPending}
      signupPlan={signupIntent?.plan === "family" || signupIntent?.plan === "business" ? signupIntent.plan : null}
      joinResult={joinInvitation.joinResult}
      onClearJoinResult={() => joinInvitation.setJoinResult(null)}
    />
  );
};

export default App;
